import type { ReactNode } from 'react';
import { setNavOpen, useUi } from '../store/ui';
import { useIsMobile, useSwipe } from '../hooks/mobile';
import { Icon, cx } from './ui';

/** ☰ in a view's header on phones: opens the servers & channels drawer. */
export function NavButton() {
  const mobile = useIsMobile();
  if (!mobile) return null;
  return (
    <button
      type="button"
      aria-label="Open navigation"
      onClick={() => setNavOpen(true)}
      className="-ml-2 rounded p-2 text-text-2 hover:text-text-0"
    >
      <Icon name="menu" />
    </button>
  );
}

/** Slide-over drawer from the left (phones). Swipe left or tap outside to close. */
export function NavDrawer({ children }: { children: ReactNode }) {
  const open = useUi((s) => s.navOpen);
  const swipe = useSwipe({ onLeft: () => setNavOpen(false) });
  return (
    <>
      <div
        aria-hidden
        onClick={() => setNavOpen(false)}
        className={cx(
          'fixed inset-0 z-40 bg-black/60 transition-opacity',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      />
      <div
        ref={swipe}
        role="dialog"
        aria-label="Navigation"
        aria-hidden={!open}
        inert={!open}
        // Opening a channel or conversation shows it; switching servers keeps the drawer open.
        onClick={(e) => {
          const href = (e.target as Element).closest('a[href]')?.getAttribute('href') ?? '';
          if (/^\/channels\/[^/]+\/[^/]+/.test(href)) setNavOpen(false);
        }}
        className={cx(
          'safe-y fixed inset-y-0 left-0 z-40 flex w-[88vw] max-w-sm bg-bg-1 shadow-2xl transition-transform duration-200',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {children}
      </div>
    </>
  );
}

/**
 * Side panels (members, pins, search, threads): inline on desktop; on phones a sheet over the
 * view: members slide in from the right, the others fill the screen.
 */
export function MobileSheet({
  onClose,
  full,
  children,
}: {
  onClose: () => void;
  full?: boolean;
  children: ReactNode;
}) {
  const mobile = useIsMobile();
  const swipe = useSwipe({ onRight: onClose }, mobile);
  if (!mobile) return <>{children}</>;
  return (
    <>
      <div aria-hidden onClick={onClose} className="fixed inset-0 z-30 bg-black/60" />
      <div
        ref={swipe}
        className={cx(
          'safe-y fixed inset-y-0 right-0 z-30 flex flex-col bg-bg-1 shadow-2xl [&>*]:w-full [&>*]:max-w-none [&>*]:flex-1',
          full ? 'left-0' : 'w-[85vw] max-w-sm',
        )}
      >
        {/* Full-screen panels have their own header with a close button. */}
        {!full && (
          <button
            type="button"
            aria-label="Close panel"
            onClick={onClose}
            className="absolute right-2 top-2 z-10 rounded p-2 text-text-2 hover:text-text-0"
          >
            <Icon name="x" />
          </button>
        )}
        {children}
      </div>
    </>
  );
}
